import React, { ReactElement, useEffect, useMemo, useRef } from 'react'
import { Profile } from 'store/api/profiles/profiles.interface'
import { SimpleActionMenuItemProps, Svg } from 'ui'
import { Flex } from '@theme-ui/components'
import { Text, ThemeUIStyleObject } from 'theme-ui'
import TextWithOverFlowAndTippyPopup from 'components/TextWithOverFlowAndTippyPopup'
import CheckIcon from 'images/analytics/check.svg'
import SimpleDropdown from 'ui/Dropdown/SimpleDropdown'
import { Setter } from 'utils'
import { useGetProfilesQuery } from 'store/api/profiles'
import { useGetUnImpersonatedUserQuery } from 'store/api/user'
import { useLocation } from '@reach/router'
import { useAppSelector } from 'store/hooks'
import { RefinementType } from 'store/analytics'
import { useGetDevicesQuery } from 'store/api/devices'
import { AnalyticsNavRelativePath } from 'components/Dashboard/NavBar/AnalyticsNavMenu'

export interface ProfileDropDownItem extends Profile {
  text: string
  isShared?: boolean
}

interface ProfileSelectionDropDownProps {
  label?: string
  selectedProfile?: ProfileDropDownItem
  setSelectedProfile:
    | Setter<ProfileDropDownItem | undefined>
    | ((selectedItem: ProfileDropDownItem) => void)
  disabledProfilePk?: string
  globalProfilePk?: string
  description?: string
  sx?: ThemeUIStyleObject
  tooltipText?: string
  testId?: string
  isDropdownDisabled?: boolean
  boundaryElementTestId?: string
  inheritedProfilePk?: string
  shouldSelectFirstProfileByDefault?: boolean
  currentProfilePk?: string
}
export default function ProfileSelectionDropDown({
  label,
  selectedProfile,
  disabledProfilePk,
  globalProfilePk,
  setSelectedProfile,
  description,
  sx,
  tooltipText,
  testId,
  boundaryElementTestId = 'parent',
  inheritedProfilePk,
  isDropdownDisabled,
  shouldSelectFirstProfileByDefault = true,
  currentProfilePk,
}: ProfileSelectionDropDownProps): ReactElement {
  const { data: profilesData } = useGetProfilesQuery('')
  const { data: devicesData } = useGetDevicesQuery('')
  const hideRef = useRef<() => void>()
  const location = useLocation()
  const selectedProfileId = useAppSelector(s => s.analytics.selectedProfileId)
  const activeRefinementEndpointData = useAppSelector(
    s => s.analytics.activeRefinements[RefinementType.ENDPOINTS][0],
  )
  const isStatistics = location.pathname.endsWith(AnalyticsNavRelativePath.STATISTICS)

  // this is going to return the unimpersonated user data which we use to figure out whether the user
  // is from parent org or sub org
  const { data: userData } = useGetUnImpersonatedUserQuery('')
  const isSubOrgUser = !!userData?.org?.parent_org

  const profileData: ProfileDropDownItem[] = useMemo(
    () =>
      [
        ...(profilesData?.profiles || []).map(p => ({ ...p, text: `${p.name}` })),
        ...(!isSubOrgUser
          ? profilesData?.shared_profiles?.map(p => ({
              ...p,
              text: `${p.name}`,
              isShared: true,
            })) || []
          : []),
      ] ?? [],
    [profilesData?.profiles, profilesData?.shared_profiles, isSubOrgUser],
  )

  const selectedDefaultProfile = useMemo(() => {
    const profileIdFromLocationState = (location.state as { profileId?: string })?.profileId
    const activeRefinementEndpointId = activeRefinementEndpointData?.value

    if (
      profileIdFromLocationState ||
      selectedProfileId ||
      activeRefinementEndpointId ||
      (!selectedProfileId && isStatistics)
    ) {
      let profileId = profileIdFromLocationState || selectedProfileId

      if (!profileId && activeRefinementEndpointId) {
        profileId = devicesData?.devices
          ?.find(device => device.PK === activeRefinementEndpointId)
          ?.profile?.PK?.toString()
      }

      return profileData.find(profile => profile.PK === profileId)
    }

    return profileData[0]
  }, [
    activeRefinementEndpointData?.value,
    devicesData?.devices,
    isStatistics,
    location.state,
    profileData,
    selectedProfileId,
  ])

  useEffect(() => {
    if (
      shouldSelectFirstProfileByDefault &&
      !selectedProfile &&
      !!selectedDefaultProfile &&
      selectedDefaultProfile.PK !== disabledProfilePk
    ) {
      setSelectedProfile(selectedDefaultProfile)
    }
  }, [
    shouldSelectFirstProfileByDefault,
    selectedDefaultProfile,
    selectedProfile,
    setSelectedProfile,
    disabledProfilePk,
  ])

  const options: SimpleActionMenuItemProps[] = useMemo(
    () =>
      profileData.map(profile => ({
        dataTestId: `testid-${profile.text}`,
        ariaLabel: `${profile?.text} option`,
        isDisabled:
          profile.PK === disabledProfilePk || (profile.PK === globalProfilePk && profile.isShared),
        isSelected: profile.PK === selectedProfile?.PK,
        isBackgroundHighlighted: true,
        searchBy: profile.text,
        buttonContent: (
          <Flex sx={{ gap: '0.2rem' }}>
            <TextWithOverFlowAndTippyPopup
              content={profile?.text ?? ''}
              ariaLabel={profile?.text ?? ''}
              sxText={{
                maxWidth: '44rem',
              }}
            />
            {currentProfilePk === profile.PK && (
              <Text variant="size14Weight400Line138Letter056">(Current)</Text>
            )}
          </Flex>
        ),
        children:
          currentProfilePk !== profile.PK ? (
            <Flex sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
              <Flex
                sx={{
                  flexDirection: ['column', 'row'],
                  justifyContent: 'space-between',
                  flex: 1,
                  alignItems: ['flex-start', 'center'],
                  mr:
                    profile.PK === selectedProfile?.PK && (profile.isShared ? '0.8rem' : '1.6rem'),
                }}
              >
                <Flex sx={{ gap: '0.8rem', alignItems: 'center' }}>
                  <TextWithOverFlowAndTippyPopup
                    variant="size15Weight600"
                    content={profile?.text ?? ''}
                    ariaLabel={profile?.text ?? ''}
                    sxText={{
                      maxWidth: '32rem',
                    }}
                  />

                  {profile.isShared && (
                    <Text
                      variant="size10Weight700"
                      className="shared-profile-badge"
                      sx={{
                        color: 'commonBlack',
                        backgroundColor: 'commonCyan800',
                        px: '0.4rem',
                        borderRadius: '10rem',
                        flexShrink: 0,
                        height: '1.6rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      {profile.PK === globalProfilePk ? 'Global' : 'Shared'}
                    </Text>
                  )}

                  {inheritedProfilePk === profile.PK.toString() && (
                    <Text
                      variant="size12Weight500"
                      className="inherited-profile-badge"
                      sx={{
                        color: 'aliceBlue60',
                        backgroundColor: 'white6',
                        px: '0.8rem',
                        borderRadius: '10rem',
                        flexShrink: 0,
                      }}
                    >
                      Inherited
                    </Text>
                  )}
                </Flex>
              </Flex>
              {profile.PK === selectedProfile?.PK && (
                <Svg
                  svg={CheckIcon}
                  sx={{ flexShrink: 0, fill: 'aliceBlue', opacity: 0.5 }}
                  descriptiveText="Selected profile indicator"
                />
              )}
            </Flex>
          ) : null,
        onClick: () => {
          setSelectedProfile(profile)
          hideRef.current?.()
        },
      })),
    [
      profileData,
      disabledProfilePk,
      globalProfilePk,
      selectedProfile?.PK,
      currentProfilePk,
      inheritedProfilePk,
      setSelectedProfile,
    ],
  )

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        pt: '1.6rem',
        '& > button': {
          px: '2.4rem',
        },
        ...sx,
      }}
      data-testid={testId ?? 'select-profile-menu'}
    >
      <SimpleDropdown
        isSearchVisible
        testId="select-profile-dropdown-desktop"
        ariaLabel="select profile dropdown"
        disabled={
          isDropdownDisabled ||
          (!profilesData?.profiles.length && !profilesData?.shared_profiles?.length)
        }
        items={options}
        hideRef={hideRef}
        label={label ?? 'Linked Profile'}
        tooltipText={tooltipText}
        sx={{ mb: description ? '0.8rem' : 0 }}
        sxDropdown={{
          ':disabled': {
            opacity: 0.5,
          },
        }}
        sxTooltipContainer={{
          maxWidth: '14rem',
        }}
        defaultValue="Select Profile"
        boundaryElementTestId={boundaryElementTestId}
        sxDropdownContent={{
          maxHeight: '19rem',
        }}
      />
      <Text sx={{ pl: '1.6rem', fontSize: '1.2rem', color: 'aliceBlue60' }}>
        {description ?? 'The Profile whose rules you want to enforce on this Endpoint'}
      </Text>
    </Flex>
  )
}
